import {useSetPaymentOptions} from 'components/customer/Confirmation/Payment/helpers/useSetPaymentOptions';
import {
    paymentTypeSet,
    gatewayAvailableSet,
    invoiceAvailableSet,
    paymentMethodSet,
    defaultPaymentMethodSet,
    accountPayAvailableSet,
    cardTypeSet,
    fullAmountSet,
    previousPaymentMethodSet,
    clearState,
} from 'components/customer/Confirmation/store/confirmationSlice';
import {useLazyFetchPaymentOptionsQuery} from 'components/customer/Confirmation/store/gatewayApi';
import {PaymentType} from 'components/manufacturer/Payments/entity/Deposit';
import {PaymentMethodType} from 'components/manufacturer/Payments/entity/Setting';
import {useNotificationContext} from 'contexts';
import {useJobContext} from 'contexts/JobContext';
import {useCallback, useEffect, useRef, useState} from 'react';
import {genericMessageHandler} from 'shared/helpers';
import {PartialJob} from 'shared/types/PartialJob';
import {useAppDispatch} from 'store/customer';

export const usePaymentOptions = () => {
    const {notify} = useNotificationContext();
    const dispatch = useAppDispatch();
    const {job} = useJobContext() as PartialJob;
    const [isLoading, setIsLoading] = useState(true);
    const [fetchPaymentOptions] = useLazyFetchPaymentOptionsQuery();
    const {setPaymentOptions} = useSetPaymentOptions();

    const blockStateUpdate = useRef(false);

    // using lazy to always refetch info disregaring cache
    const setOptions = useCallback(
        async (jobID: number) => {
            try {
                const {data, isError} = await fetchPaymentOptions(
                    {jobID},
                    false
                );

                if (isError) {
                    throw new Error(
                        'Could not load payment options please try again later'
                    );
                }

                const paymentSettings = data.paymentSettings;
                dispatch(
                    invoiceAvailableSet(paymentSettings.enableInvoicePayments)
                );
                dispatch(
                    gatewayAvailableSet(paymentSettings.enableCardPayments)
                );
                dispatch(
                    accountPayAvailableSet(paymentSettings.allowPayOnAccount)
                );

                dispatch(paymentMethodSet(data.defaultPaymentMethod));
                dispatch(defaultPaymentMethodSet(data.defaultPaymentMethod));
                dispatch(fullAmountSet(data.fullAmount));
                setPaymentOptions(data.paymentOptions, job.taxRate);
                dispatch(cardTypeSet(data.defaultPaymentMethodType));

                if (
                    data?.paymentSummary?.paymentMethod &&
                    !blockStateUpdate.current
                ) {
                    dispatch(
                        previousPaymentMethodSet(
                            data.paymentSummary.paymentMethod
                        )
                    );
                }

                let defaultPaymenType: PaymentType;

                switch (data.defaultPaymentMethod) {
                    case PaymentMethodType.CARD_DEPOSIT:
                    case PaymentMethodType.CARD_FULL:
                        defaultPaymenType = PaymentType.GATEWAY;
                        break;
                    case PaymentMethodType.INVOICE_DEPOSIT:
                    case PaymentMethodType.INVOICE_FULL:
                        defaultPaymenType = PaymentType.INVOICE;
                        break;
                    case PaymentMethodType.ACCOUNT:
                        defaultPaymenType = PaymentType.ACCOUNT;
                        break;
                }

                if (
                    defaultPaymenType == PaymentType.GATEWAY &&
                    !paymentSettings.enableCardPayments
                ) {
                    defaultPaymenType = PaymentType.INVOICE;
                }

                if (
                    defaultPaymenType == PaymentType.INVOICE &&
                    !paymentSettings.enableInvoicePayments
                ) {
                    defaultPaymenType = PaymentType.GATEWAY;
                }

                dispatch(paymentTypeSet(defaultPaymenType));
            } catch (e) {
                genericMessageHandler(notify, e);
            } finally {
                setIsLoading(false);
            }
        },
        [dispatch, fetchPaymentOptions, job, notify, setPaymentOptions]
    );

    useEffect(() => {
        if (job && job.id) {
            void setOptions(job.id);
        }

        return () => {
            blockStateUpdate.current = true;
            dispatch(clearState());
        };
    }, [job]);

    return {
        isLoading,
    };
};
